// ============================================================================
// WorkbenchFindings.jsx
// Findings list — accordion + compact table toggle.
// ============================================================================

import React, { useState, useMemo } from 'react';
import {
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Grid3X3,
  Lightbulb,
  List,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  FINDING_AREAS, SEVERITIES, CONFIDENCE, VALUES, EFFORTS, FINDING_STATUSES,
  normaliseFinding, splitLines, statusTone, focusLabel,
} from '@process-advisor/process-miner/consultant/workbenchShared';
import * as S from '@process-advisor/process-miner/consultant/workbenchStyles';

// ── Shared tiny components ──────────────────────────────────────────────────

const StatusPill = ({ children, tone = 'neutral' }) => (
  <span style={S.statusPillStyle(tone)}>{children}</span>
);

const Select = ({ value, onChange, options }) => (
  <select style={S.SELECT} value={value || ''} onChange={(e) => onChange(e.target.value)}>
    {options.map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);

const Field = ({ label, value, onChange, placeholder = '', rows = 1, style = {} }) => (
  <label style={{ ...S.FIELD, ...style }}>
    <span style={S.FIELD_LABEL}>{label}</span>
    {rows > 1
      ? <textarea style={S.TEXTAREA} value={value || ''} placeholder={placeholder} rows={rows} onChange={(e) => onChange?.(e.target.value)} />
      : <input    style={S.INPUT}    value={value || ''} placeholder={placeholder}             onChange={(e) => onChange?.(e.target.value)} />
    }
  </label>
);

// ── Accordion row ───────────────────────────────────────────────────────────

const FindingAccordion = ({ finding, expanded, onToggle, onUpdate, onRemove, onConvert }) => (
  <div style={S.LIST_ITEM}>
    <div
      style={S.LIST_SUMMARY}
      onClick={onToggle}
      onMouseEnter={(e) => { e.currentTarget.style.background = S.LIST_SUMMARY_HOVER.background; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
    >
      <div>{expanded ? <ChevronDown size={18} color={S.C.N300} /> : <ChevronRight size={18} color={S.C.N300} />}</div>
      <div style={{ minWidth: 0 }}>
        <div style={S.ITEM_TITLE}>{finding.title || 'Untitled finding'}</div>
        <div style={S.ITEM_SUBTITLE}>{focusLabel(finding) || 'No focus selected yet'}</div>
      </div>
      <div style={S.CHIP_ROW}>
        <StatusPill tone="neutral">{finding.area}</StatusPill>
        <StatusPill tone="neutral">{finding.severity}</StatusPill>
        <StatusPill tone={statusTone(finding.status)}>{finding.status}</StatusPill>
      </div>
      <div style={S.ITEM_ACTIONS} onClick={(e) => e.stopPropagation()}>
        <button style={S.ICON_BUTTON_PRIMARY} title="Convert to action" onClick={onConvert}><ArrowRight size={15} /></button>
        <button style={S.ICON_BUTTON} title="Remove finding" onClick={onRemove}><Trash2 size={15} /></button>
      </div>
    </div>
    {expanded && (
      <div style={S.ITEM_BODY}>
        <div style={S.ITEM_GRID_FINDING}>
          <Field label="Title" value={finding.title || ''} onChange={(v) => onUpdate({ title: v, finding: v })} />
          <label style={S.FIELD}><span style={S.FIELD_LABEL}>Area</span><Select value={finding.area} onChange={(v) => onUpdate({ area: v })} options={FINDING_AREAS} /></label>
          <label style={S.FIELD}><span style={S.FIELD_LABEL}>Severity</span><Select value={finding.severity} onChange={(v) => onUpdate({ severity: v })} options={SEVERITIES} /></label>
          <label style={S.FIELD}><span style={S.FIELD_LABEL}>Status</span><Select value={finding.status} onChange={(v) => onUpdate({ status: v })} options={FINDING_STATUSES} /></label>
        </div>
        <div style={S.FORM_GRID}>
          <Field style={{ gridColumn: '1 / -1' }} label="Evidence observed" value={(finding.evidence || []).join('\n')} onChange={(v) => onUpdate({ evidence: splitLines(v) })} rows={3} placeholder="What evidence supports the finding?" />
          <Field style={{ gridColumn: '1 / -1' }} label="Why it matters / recommendation" value={finding.recommendation || ''} onChange={(v) => onUpdate({ recommendation: v })} rows={3} placeholder="Why this matters and what should be considered" />
          <label style={S.FIELD}><span style={S.FIELD_LABEL}>Confidence</span><Select value={finding.confidence} onChange={(v) => onUpdate({ confidence: v })} options={CONFIDENCE} /></label>
          <label style={S.FIELD}><span style={S.FIELD_LABEL}>Value potential</span><Select value={finding.valuePotential} onChange={(v) => onUpdate({ valuePotential: v })} options={VALUES} /></label>
          <Field label="Proposed action (optional)" value={finding.action || ''} onChange={(v) => onUpdate({ action: v })} />
          <Field label="Owner (optional)" value={finding.owner || ''} onChange={(v) => onUpdate({ owner: v })} />
          <Field style={{ gridColumn: '1 / -1' }} label="Consultant notes" value={finding.consultantNotes || ''} onChange={(v) => onUpdate({ consultantNotes: v })} rows={3} />
        </div>
      </div>
    )}
  </div>
);

// ── Table row ───────────────────────────────────────────────────────────────

const FindingTableRow = ({ finding, onSelect, onConvert, onRemove }) => (
  <tr>
    <td style={{ ...S.TD, ...S.TD_TITLE }} onClick={onSelect}>{finding.title || 'Untitled finding'}</td>
    <td style={S.TD}>{finding.area}</td>
    <td style={S.TD}><StatusPill tone="neutral">{finding.severity}</StatusPill></td>
    <td style={S.TD}><StatusPill tone={statusTone(finding.status)}>{finding.status}</StatusPill></td>
    <td style={S.TD}>
      <div style={{ display: 'flex', gap: 6 }}>
        <button style={S.ICON_BUTTON_PRIMARY} title="Convert to action" onClick={onConvert}><ArrowRight size={14} /></button>
        <button style={S.ICON_BUTTON} title="Remove" onClick={onRemove}><Trash2 size={14} /></button>
      </div>
    </td>
  </tr>
);

// ── Main component ──────────────────────────────────────────────────────────

const WorkbenchFindings = ({
  workspace = {},
  onUpdateFinding,
  onRemoveFinding,
  onAddFinding,
  onConvertFinding,
}) => {
  const [view, setView] = useState('list');       // 'list' | 'table'
  const [expanded, setExpanded] = useState({});
  const findings = workspace.findings || [];

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div style={S.ROOT}>
      <div style={S.SHELL}>
        <div style={S.CARD}>

          <div style={S.CARD_HEADER}>
            <div style={S.CARD_TITLE_ROW}>
              <Lightbulb size={17} color="#FF8B00" />
              <div style={S.CARD_TITLE}>Findings</div>
              <span style={S.PILL}>{findings.length}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {/* View toggle */}
              {findings.length > 0 && (
                <div style={{ display: 'flex', border: `1px solid ${S.C.N40}`, borderRadius: 8, overflow: 'hidden' }}>
                  <button
                    style={{ ...S.ICON_BUTTON, border: 'none', borderRadius: 0, background: view === 'list' ? S.C.B50 : S.C.N0 }}
                    title="Accordion view"
                    onClick={() => setView('list')}
                  ><List size={15} color={view === 'list' ? S.C.B400 : S.C.N300} /></button>
                  <button
                    style={{ ...S.ICON_BUTTON, border: 'none', borderRadius: 0, borderLeft: `1px solid ${S.C.N40}`, background: view === 'table' ? S.C.B50 : S.C.N0 }}
                    title="Table view"
                    onClick={() => setView('table')}
                  ><Grid3X3 size={15} color={view === 'table' ? S.C.B400 : S.C.N300} /></button>
                </div>
              )}
              <button style={S.BUTTON_SECONDARY} onClick={onAddFinding}><Plus size={15} /> Add Finding</button>
            </div>
          </div>

          <div style={S.CARD_BODY}>
            {findings.length === 0 && (
              <div style={S.EMPTY}>
                No findings saved yet. Use Save Finding from Advisor, Evidence, or Explorer. Findings should be concise observations backed by evidence.
              </div>
            )}

            {findings.length > 0 && view === 'list' && (
              <div style={S.LIST}>
                {findings.map((f) => (
                  <FindingAccordion
                    key={f.id}
                    finding={f}
                    expanded={!!expanded[f.id]}
                    onToggle={() => toggle(f.id)}
                    onUpdate={(patch) => onUpdateFinding(f.id, patch)}
                    onRemove={() => onRemoveFinding(f.id)}
                    onConvert={() => onConvertFinding(f)}
                  />
                ))}
              </div>
            )}

            {findings.length > 0 && view === 'table' && (
              <div style={{ overflowX: 'auto' }}>
                <table style={S.TABLE}>
                  <thead>
                    <tr>
                      <th style={S.TH}>Finding</th>
                      <th style={S.TH}>Area</th>
                      <th style={S.TH}>Severity</th>
                      <th style={S.TH}>Status</th>
                      <th style={{ ...S.TH, width: 90 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {findings.map((f) => (
                      <FindingTableRow
                        key={f.id}
                        finding={f}
                        onSelect={() => { setView('list'); setExpanded(prev => ({ ...prev, [f.id]: true })); }}
                        onConvert={() => onConvertFinding(f)}
                        onRemove={() => onRemoveFinding(f.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default WorkbenchFindings;
